import { Component, Injector, Output, EventEmitter } from '@angular/core';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';

import { Observable } from 'rxjs';
import { IMenuStructure } from '../../models/rights/i-menu-structure';
import { AccessGroupsService } from '../../services/access-groups.service';
import { Menu } from '../../models/rights/menu';

export class FlatStructure {
    level: number;
    expandable: boolean;
    origin: IMenuStructure;
}

@Component({
    selector: 'access-tree',
    templateUrl: './access-tree.component.html',
    styleUrls: ['./access-tree.component.scss']
})
export class AccessTreeComponent {
    @Output() checkChanged: EventEmitter<any> = new EventEmitter();
    protected readonly _service: AccessGroupsService;

    //private flatStructureMap = new Map<FlatStructure, IMenuStructure>();
    private nestedNodeMap = new Map<IMenuStructure, FlatStructure>();

    private treeFlattener: MatTreeFlattener<IMenuStructure, FlatStructure>;

    dataSource: MatTreeFlatDataSource<IMenuStructure, FlatStructure>;
    treeControl: FlatTreeControl<FlatStructure>;

    /** The selection for checklist */
    //checklistSelection = new SelectionModel<FlatStructure>(true /* multiple */);

    constructor(injector: Injector) {
        this._service = injector.get(AccessGroupsService);

        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
        this.treeControl = new FlatTreeControl<FlatStructure>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    }

    initialize(accessGroupId: number, expandTree?: boolean): void {
        this._service.getMenus(accessGroupId).subscribe(res => {
            var compareFn = (a: IMenuStructure, b: IMenuStructure) => { return (a.rank < b.rank) ? -1 : ((a.rank > b.rank) ? 1 : 0); };
            this.dataSource.data = res.sort(compareFn);
            if (expandTree) this.treeControl.expandAll();
        });
    }

    save(accessGroupId: number): Observable<any> {
        return this._service.setMenus(accessGroupId, this.dataSource.data as Menu[]);
    }

    toggle(node: FlatStructure) {
        if (!node.expandable) return;
        if (!this.treeControl.isExpanded(node)) this.treeControl.expand(node);
        else this.treeControl.collapse(node);
    }

    getLevel = (node: FlatStructure) => node.level;

    isExpandable = (node: FlatStructure) => node.expandable;

    getChildren = (node: IMenuStructure): IMenuStructure[] => node.children;

    hasChild = (_: number, _nodeData: FlatStructure) => _nodeData.expandable;

    /**
    * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
    */
    transformer = (node: IMenuStructure, level: number) => {
        let flatStructure: FlatStructure = this.nestedNodeMap.get(node);
        if (!flatStructure || flatStructure.origin !== node) {
            flatStructure = new FlatStructure();

            flatStructure.level = level;
            flatStructure.expandable = node.children && node.children.length > 0;
            flatStructure.origin = node;
        }

        this.nestedNodeMap.set(node, flatStructure);

        return flatStructure;
    }

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    selectionToggle(node: FlatStructure): void {
        const descendants = this.treeControl.getDescendants(node);
        if (descendants) descendants.forEach(d => { if (!d.origin.restrictionExplanation) d.origin.isOwned = node.origin.isOwned; });

        let parent: FlatStructure = this.getParentNode(node);
        let state: boolean;
        while (parent) {
            if (state !== null) state = this.checkState(parent);
            parent.origin.isOwned = state;
            parent = this.getParentNode(parent);
        }

        this.checkChanged.emit(node.origin);
    }

    checkState(node: FlatStructure): boolean {
        const descendants: FlatStructure[] = this.treeControl.getDescendants(node);
        const checkedCount = descendants.filter(d => d.origin.isOwned === true).length;

        if (checkedCount === 0) return false;
        else if (descendants.length === checkedCount) return true;
        else return null;
    }

    /* Get the parent node of a node */
    getParentNode(node: FlatStructure): FlatStructure | null {
        const currentLevel = this.getLevel(node);

        if (currentLevel < 1) {
            return null;
        }

        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];

            if (this.getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }
}
